/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Typography, Box, Card, useTheme } from '@mui/material';
import { CustomTooltip } from '@opengeoweb/shared';
import { Visibility, VisibilityOff } from '@opengeoweb/theme';
import { useWebmapReactTranslation } from '../../utils/i18n';

interface LegendLayoutProps {
  title: string;
  children: React.ReactNode;
  height?: number;
  width?: number;
  showLayerLegend?: boolean;
  toggleLayerLegendVisibility?: (showLegend: boolean) => void;
}

export const LegendLayout: React.FC<LegendLayoutProps> = ({
  title,
  children,
  height,
  width,
  showLayerLegend = true,
  toggleLayerLegendVisibility,
}) => {
  const { t } = useWebmapReactTranslation();
  const theme = useTheme();

  return (
    <Card
      data-testid="legend"
      sx={{
        // eslint-disable-next-line no-nested-ternary
        border: toggleLayerLegendVisibility
          ? showLayerLegend
            ? `1px solid ${theme.palette.geowebColors.cards.cardContainerBorder}`
            : `1px solid ${theme.palette.geowebColors.cards.cardContainerDisabledBorder}`
          : 'none',
        // eslint-disable-next-line no-nested-ternary
        backgroundColor: toggleLayerLegendVisibility
          ? showLayerLegend
            ? 'geowebColors.cards.cardContainer'
            : 'geowebColors.cards.cardContainerDisabled'
          : '#FFF',
        overflow: 'hidden',
        marginBottom: 0.5,
        boxShadow: 0,
        borderRadius: '3px',
        padding: toggleLayerLegendVisibility ? 'none' : '10px',
      }}
    >
      {toggleLayerLegendVisibility ? (
        <Box data-testid="legend-title">
          <CustomTooltip
            title={
              showLayerLegend
                ? t('webmap-react-hide-layer')
                : t('webmap-react-show-layer')
            }
          >
            <Typography
              alignItems="center"
              component="button"
              display="flex"
              gap={1}
              onClick={(event): void => {
                event.stopPropagation();
                toggleLayerLegendVisibility(!showLayerLegend);
              }}
              sx={{
                fontFamily: 'Roboto, Helvetica, Arial',
                fontSize: '12px',
                width: `${width}px`,
                minWidth: '100%',
                padding: '4px',
                wordWrap: 'break-word',
                color: showLayerLegend
                  ? 'geowebColors.typographyAndIcons.text'
                  : 'geowebColors.typographyAndIcons.inactiveText',
                whiteSpace: 'nowrap',
                border: 'none',
                backgroundColor: showLayerLegend
                  ? 'geowebColors.cards.cardContainer'
                  : 'geowebColors.cards.cardContainerDisabled',
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
                },
              }}
              variant="subtitle2"
            >
              {showLayerLegend ? (
                <Visibility fontSize="medium" sx={{ margin: 'auto 0px' }} />
              ) : (
                <VisibilityOff fontSize="medium" sx={{ margin: 'auto 0px' }} />
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </span>
            </Typography>
          </CustomTooltip>
        </Box>
      ) : (
        <Box data-testid="legend-title" sx={{ backgroundColor: '#FFF' }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, Helvetica, Arial',
              fontSize: '10px',
              minWidth: '100%',
              wordWrap: 'break-word',
              color: 'common.black',
              whiteSpace: 'nowrap',
            }}
            variant="h3"
          >
            {title}
          </Typography>
        </Box>
      )}

      {!!showLayerLegend && (
        <CustomTooltip placement="top" title={title || 'loading'}>
          <Box
            sx={{
              margin: toggleLayerLegendVisibility
                ? '2px 0 8px 12px'
                : '10px 0px 10px 0px',
              height: `${height}px`,
              width: `${width}px`,
              minWidth: '100%',
              backgroundColor: '#FFF',
            }}
          >
            {children}
          </Box>
        </CustomTooltip>
      )}
    </Card>
  );
};
